<template>
  <div class="page">
    <div class="coupons">
      <div class="coupons__toolbar">
        <el-radio-group v-model="statusFilter" size="small" @change="loadList">
          <el-radio-button label="">{{ $t('coupons.filterAll') }}</el-radio-button>
          <el-radio-button label="unused">{{ $t('coupons.filterUnused') }}</el-radio-button>
          <el-radio-button label="used">{{ $t('coupons.filterUsed') }}</el-radio-button>
          <el-radio-button label="expired">{{ $t('coupons.filterExpired') }}</el-radio-button>
        </el-radio-group>
        <el-button plain :loading="loading" @click="loadList">{{ $t('orders.refresh') }}</el-button>
      </div>

      <div v-if="!useBackend()" class="coupons__empty">{{ $t('orders.needBackend') }}</div>
      <div v-else-if="!isLoggedIn" class="coupons__empty">{{ $t('orders.needLogin') }}</div>
      <div v-else v-loading="loading" class="coupons__list">
        <article v-for="row in list" :key="row.id" class="coupon-card" :class="'is-' + row.status">
          <div class="coupon-card__value">
            <small>¥</small>
            <strong>{{ formatMoney(row.template && row.template.discount_value) }}</strong>
          </div>
          <div class="coupon-card__main">
            <h3>{{ (row.template && row.template.name) || $t('coupons.unnamed') }}</h3>
            <p>{{ scopeText(row) }}</p>
            <p class="coupon-card__meta">
              {{ $t('coupons.validTo') }}：{{ formatTime(row.valid_end_at) }}
            </p>
            <p class="coupon-card__code">{{ row.coupon_code }}</p>
          </div>
          <el-tag size="small" :type="statusType(row.status)">{{ statusText(row.status) }}</el-tag>
        </article>
        <el-empty v-if="loaded && !loading && list.length === 0" :description="$t('coupons.empty')" />
      </div>
    </div>
  </div>
</template>

<script>
import { getAuthHeader, isLoggedIn as authIsLoggedIn, sessionState } from '@/utils/auth'

function useBackend() {
  return process.env.VUE_APP_USE_BACKEND === 'true'
}

function apiUrl(path) {
  const root = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  return root ? `${root}${p}` : p
}

export default {
  name: 'CouponsPage',
  data() {
    return {
      loading: false,
      loaded: false,
      list: [],
      statusFilter: 'unused'
    }
  },
  computed: {
    isLoggedIn() {
      void sessionState.user
      return authIsLoggedIn()
    }
  },
  mounted() {
    if (useBackend() && this.isLoggedIn) this.loadList()
  },
  methods: {
    useBackend,
    formatMoney(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '0.00'
    },
    formatTime(v) {
      if (!v) return '—'
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      return d.toLocaleString()
    },
    scopeText(row) {
      const t = row && row.template
      if (!t) return ''
      if (t.scope_type === 'game') return this.$t('coupons.scopeGame')
      return this.$t('coupons.scopeAll')
    },
    statusType(s) {
      if (s === 'unused') return 'success'
      if (s === 'used') return 'info'
      if (s === 'expired') return 'warning'
      return ''
    },
    statusText(s) {
      const map = {
        unused: this.$t('coupons.statusUnused'),
        used: this.$t('coupons.statusUsed'),
        expired: this.$t('coupons.statusExpired'),
        locked: this.$t('coupons.statusLocked')
      }
      return map[s] || s
    },
    async loadList() {
      if (!useBackend() || !this.isLoggedIn) return
      this.loading = true
      try {
        const qs = new URLSearchParams()
        if (this.statusFilter) qs.set('status', this.statusFilter)
        const res = await fetch(`${apiUrl('/api/marketing/coupons')}?${qs.toString()}`, {
          headers: { ...getAuthHeader() }
        })
        const data = await res.json().catch(() => ({}))
        if (!data.ok) {
          this.$message.error(data.message || this.$t('coupons.loadFailed'))
          return
        }
        this.list = Array.isArray(data.list) ? data.list : []
        this.loaded = true
      } catch (e) {
        this.$message.error(this.$t('recharge.network'))
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.coupons__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.coupons__empty {
  padding: 48px 16px;
  text-align: center;
  color: #64748b;
}
.coupons__list {
  display: grid;
  gap: 12px;
}
.coupon-card {
  display: grid;
  grid-template-columns: 110px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #fff7ed, #ffffff);
  border: 1px solid #fed7aa;
}
.coupon-card.is-used,
.coupon-card.is-expired {
  opacity: 0.65;
  filter: grayscale(0.2);
}
.coupon-card__value {
  text-align: center;
  color: #c2410c;
}
.coupon-card__value small {
  font-size: 14px;
}
.coupon-card__value strong {
  font-size: 28px;
  font-weight: 700;
}
.coupon-card__main h3 {
  margin: 0 0 4px;
  font-size: 16px;
}
.coupon-card__main p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}
.coupon-card__code {
  font-family: ui-monospace, monospace;
  margin-top: 4px !important;
}
</style>
